import React, { useEffect, useState, useRef } from 'react';

import { TierProvider } from '../../contexts/TierContext';
import { PageBanner } from '../../components/PageBanner/PageBanner';
import { PageNavigation, Tab } from '../../components/PageNavigation/PageNavigation';
import { PointsOverview } from './components/PointsOverview/PointsOverview';
import { EngagementEvents } from './components/EngagementEvents/EngagementEvents';
import { LoyaltyCard } from './components/LoyaltyCard/LoyaltyCard';
import { StatsCard } from './components/StatsCard/StatsCard';
import History from './components/History/History';
import type { HistoryRef } from './components/History/History';
import { Loader } from '../../components/Loader/Loader';
import { UserProfile } from '../../types/user.types';
import './DashboardPage.scss';
import { useTranslation } from 'react-i18next';
import { userService } from '../../services/user.service';
import { useHistory } from '@/hooks/useHistory';

// Use our user service to fetch real user data
const fetchUserData = async (): Promise<UserProfile> => {
  return userService.getCurrentUser();
};

const DashboardPage: React.FC = () => {
  const historyRef = useRef<HistoryRef>(null);
  const { t } = useTranslation('home');
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const { tabCounts } = useHistory('all', '');

  const dashboardTabs: Tab[] = [
    { id: 'overview', label: t('navigation.overview') },
    { id: 'history', label: t('navigation.history') }
  ];

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const userData = await fetchUserData();
        setUser(userData);
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, []);

  if (loading) {
    return <Loader variant="fullscreen" />;
  }

  const currentTier = (user?.tier as any) || 'bronze';
  
  return (
    <TierProvider tier={currentTier}>
      <div className={`dashboard-page dashboard-page--${currentTier}`}>
        <PageBanner userName={user?.firstName || ''} userData={user!} />
        <div className="dashboard-page__content">
          <PageNavigation 
            activeTab={activeTab} 
            onTabChange={setActiveTab}
            tabs={dashboardTabs}
          />
          <div className="dashboard-page__inner-content">
            <div className={`dashboard-page__full-width ${activeTab !== 'history' ? 'dashboard-page__full-width--hidden' : ''}`}>
              <History ref={historyRef} />
            </div>
            {activeTab === 'overview' && user?.points && (
              <>
                <div className="dashboard-page__left-column">
                  <PointsOverview
                    projectValue={user.points.projectValue}
                    engagement={user.points.engagement}
                  />
                </div>
                <div className="dashboard-page__right-column">
                  <div className="dashboard-page__top-section">
                    <div className="dashboard-page__top-left">
                      <LoyaltyCard
                        memberCardNumber={user.accountInformations?.membershipNumber || ''}
                        memberName={`${user.firstName} ${user.lastName}`}
                        memberSince={user.accountInformations?.enrollmentDate?.substring(0, 4) || '2024'}
                      />
                    </div>
                    <div className="dashboard-page__top-right">
                      <div className="dashboard-page__stats-column">
                        <StatsCard
                          title={t('dashboard.stats.salesTagged')}
                          value={tabCounts.specification}
                          onClick={() => {
                            setActiveTab('history');
                            historyRef.current?.setActiveTab('specification');
                          }}
                        />
                        <StatsCard
                          title={t('dashboard.stats.engagementActionsCompleted')}
                          value={tabCounts.engagement}
                          onClick={() => {
                            setActiveTab('history');
                            historyRef.current?.setActiveTab('engagement');
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <>
                    <EngagementEvents />
                  </>
                </div>
              </>
            )}
            {activeTab === 'redemption' && (
              <div>Redemption content coming soon...</div>
            )}
          </div>
        </div>
      </div>
    </TierProvider>
  );
};

export default DashboardPage;
